// اجزای مشترک خرید و پرداختنی AP-INV-03 (SCR-STAFF-876 و 877): مسیر، کلیدهای Query، وضعیت‌ها و رنگشان، تبدیل مبلغ ریالی ورودی و فهرست
// تأمین‌کنندگان فعال برای انتخاب.
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type InvItemUnit,
  type InvItemView,
  type InvPurchaseInvoiceStatus,
  type InvPurchaseOrderStatus,
  type InvPurchaseRequestStatus,
  type InvSupplierChequeStatus,
  type InvSupplierPaymentMethod,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { FormDialog, latinDigits, Money, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

export const PURCHASING_PATH = '/inventory/purchasing';
export const SUPPLIERS_PATH = '/inventory/suppliers';

export const REQUEST_STATUSES: readonly InvPurchaseRequestStatus[] = ['Draft', 'PendingApproval', 'Approved', 'Rejected', 'Ordered', 'Cancelled'];
export const ORDER_STATUSES: readonly InvPurchaseOrderStatus[] = ['Approved', 'Issued', 'PartiallyReceived', 'Received', 'Invoiced', 'Cancelled'];
export const INVOICE_STATUSES: readonly InvPurchaseInvoiceStatus[] = ['Draft', 'Posted', 'Cancelled'];
export const PAYMENT_METHODS: readonly InvSupplierPaymentMethod[] = ['Cash', 'BankTransfer', 'Cheque'];

export const procurementKeys = {
  requests: (tenantId: string | undefined) => ['inventory', 'purchase-requests', tenantId] as const,
  orders: (tenantId: string | undefined) => ['inventory', 'purchase-orders', tenantId] as const,
  invoices: (tenantId: string | undefined) => ['inventory', 'purchase-invoices', tenantId] as const,
  payables: (tenantId: string | undefined) => ['inventory', 'payables', tenantId] as const,
  payments: (tenantId: string | undefined) => ['inventory', 'supplier-payments', tenantId] as const,
  cheques: (tenantId: string | undefined) => ['inventory', 'supplier-cheques', tenantId] as const,
  contracts: (tenantId: string | undefined) => ['inventory', 'supply-contracts', tenantId] as const,
  analysis: (tenantId: string | undefined) => ['inventory', 'purchase-price-analysis', tenantId] as const,
};

export function requestTone(status: InvPurchaseRequestStatus): StatusTone {
  switch (status) {
    case 'Approved':
    case 'Ordered':
      return 'done';
    case 'PendingApproval':
      return 'pending';
    case 'Draft':
      return 'inactive';
    default:
      return 'cancelled';
  }
}

export function orderTone(status: InvPurchaseOrderStatus): StatusTone {
  switch (status) {
    case 'Received':
    case 'Invoiced':
      return 'done';
    case 'Approved':
      return 'inactive';
    case 'Cancelled':
      return 'cancelled';
    default:
      return 'pending';
  }
}

export function invoiceTone(status: InvPurchaseInvoiceStatus): StatusTone {
  return status === 'Posted' ? 'done' : status === 'Draft' ? 'pending' : 'cancelled';
}

export function chequeTone(status: InvSupplierChequeStatus): StatusTone {
  return status === 'Cleared' ? 'done' : status === 'Issued' ? 'pending' : 'cancelled';
}

/** مبلغ ریالی ورودی کاربر (ارقام فارسی یا لاتین، با جداکننده) به عدد صحیح ریال؛ خالی یا نامعتبر ← null (ADR-013). */
export function rialsOf(text: string): number | null {
  const normalized = latinDigits(text.trim()).replace(/[,٬\s]/g, '');
  return /^[0-9]{1,15}$/.test(normalized) ? Number(normalized) : null;
}

/** واحدهای قابل انتخاب کالا (پایه، خرید، مصرف و تبدیل‌ها). */
export function unitsOf(item: InvItemView | undefined): InvItemUnit[] {
  if (!item) {
    return [];
  }
  return [...new Set<InvItemUnit>([item.baseUnit, item.purchaseUnit, item.consumptionUnit, ...item.conversions.map((conversion) => conversion.unit)])];
}

/** نمایش مبلغ. */
export function MoneyCell({ value }: { value: { amount: number | string; currency: string } | null | undefined }) {
  return value ? <Money amountMinor={Number(value.amount)} currency={value.currency} /> : <>—</>;
}

/** تأمین‌کنندگان فعال برای انتخاب. */
export function useSupplierOptions(enabled = true) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: ['inventory', 'suppliers', session?.tenantId, 'procurement-options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/suppliers', { params: { query: { active: true, limit: 100 } } })),
    enabled,
  });
}

/** انتخاب تأمین‌کننده (کنترل‌شده). */
export function SupplierSelect({
  value,
  onChange,
  testId,
  required = false,
  emptyLabelKey,
}: {
  value: string;
  onChange: (id: string) => void;
  testId: string;
  required?: boolean;
  emptyLabelKey?: string;
}) {
  const { t } = useTranslation('inv');
  const suppliers = useSupplierOptions();
  return (
    <TextField
      select
      size="small"
      required={required}
      label={t('inv:purchasing.supplier')}
      value={suppliers.data?.items.some((supplier) => supplier.id === value) ? value : ''}
      onChange={(event) => onChange(event.target.value)}
      sx={{ minWidth: 240 }}
      slotProps={{ htmlInput: { 'data-testid': testId } }}
    >
      {emptyLabelKey && <MenuItem value="">{t(emptyLabelKey)}</MenuItem>}
      {(suppliers.data?.items ?? []).map((supplier) => (
        <MenuItem key={supplier.id} value={supplier.id}>
          {supplier.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** علت رد، لغو یا برگشت (اختیاری یا الزامی). */
export function ReasonDialog({
  titleKey,
  screenId,
  required = false,
  onClose,
  onConfirm,
  children,
}: {
  titleKey: string;
  screenId: string;
  required?: boolean;
  onClose: () => void;
  onConfirm: (reason: string | null, idempotencyKey: string) => Promise<void>;
  children?: ReactNode;
}) {
  const { t } = useTranslation('inv');
  const idempotency = useIdempotencyKey();
  const [reason, setReason] = useState('');
  const confirm = useMutation({
    mutationFn: () => onConfirm(reason.trim() || null, idempotency.current()),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
  });
  return (
    <FormDialog
      open
      screenId={screenId}
      titleKey={titleKey}
      onSubmit={() => (required && !reason.trim() ? undefined : confirm.mutate())}
      onClose={onClose}
      isDirty={Boolean(reason)}
      isSubmitting={confirm.isPending}
      submitError={confirm.error}
    >
      {children}
      <TextField size="small" required={required} label={t('inv:purchasing.reason')} value={reason} onChange={(event) => setReason(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'reason-input' } }} />
    </FormDialog>
  );
}
